import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
    Users, MessageCircle, Heart, Repeat2, Bookmark, 
    Loader2, Plus, Search, Filter, BookOpen, Quote, Sparkles, ChevronLeft
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { socialService } from '../services/socialService';
import AvatarRenderer from '../components/AvatarRenderer';
import PostDetailModal from '../components/PostDetailModal';
import { extractPostStyle } from '../utils/postUtils';
import { AppBackButton } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { CommunityLinks } from '../components/CommunityLinks';
import { StudyPageHeader } from '../components/StudyPageHeader';

// Helpers para cache instantâneo SWR
const getLocalCache = (key: string) => {
  try {
    const raw = localStorage.getItem(`libritui_swr_${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const setLocalCache = (key: string, data: any) => {
  try {
    localStorage.setItem(`libritui_swr_${key}`, JSON.stringify(data));
  } catch {}
};

const CommunityFeedPage: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { isDarkMode, accentColor } = useTheme();
    const navigate = useNavigate();

    const { postId } = useParams();
    
    // Inicia com cache instantâneo se disponível
    const initialPosts = getLocalCache('community_feed_posts');
    const [posts, setPosts] = useState<any[]>(initialPosts || []);
    const [loading, setLoading] = useState(!initialPosts);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const LIMIT = 10;
    
    const [selectedPost, setSelectedPost] = useState<any | null>(null);
    const [filter, setFilter] = useState<'all' | 'books' | 'friends'>('all');
    const [userInteractions, setUserInteractions] = useState<{ [postId: string]: { like?: boolean, save?: boolean } }>({});

    useEffect(() => {
        setOffset(0);
        setHasMore(true);
        fetchPosts(0, true);
        if (user) fetchUserInteractions();
    }, [filter, user]);

    useEffect(() => {
        const handleScroll = () => {
            if (loading || loadingMore || !hasMore) return;
            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
            const clientHeight = document.documentElement.clientHeight || window.innerHeight;
            if (scrollTop + clientHeight >= scrollHeight * 0.8) {
                loadMore();
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, loadingMore, hasMore, offset]);

    // Deep linking: Open post if ID is in URL
    useEffect(() => {
        if (postId && posts.length > 0 && !selectedPost) {
            const post = posts.find(p => p.id === postId);
            if (post) {
                setSelectedPost(post);
            } else {
                navigate('/community-feed', { replace: true }); // Post not found
            }
        }
    }, [postId, posts]);

    const fetchUserInteractions = async () => {
        if (!user) return;
        try {
            const data = await socialService.getPostInteractionsForUser(user.id);
            const map: any = {};
            (data || []).forEach((i: any) => {
                if (!map[i.post_id]) map[i.post_id] = {};
                map[i.post_id][i.type] = true;
            });
            setUserInteractions(map);
        } catch (e) {
            console.error("Error fetching interactions:", e);
        }
    };

    const deepFetchPostsData = async (newPosts: any[]) => {
        if (!newPosts || newPosts.length === 0) return;
        
        try {
            // Fetch comments and reactions for all these posts in parallel
            await Promise.all(newPosts.map(async (p) => {
                try {
                    await socialService.getPostComments(p.id, user?.id);
                } catch (e) {
                    console.warn(`Deep fetch failed for post ${p.id}`, e);
                }
            }));
            // Note: userInteractions is already fetched globally for the user via fetchUserInteractions,
            // which populates InteractionCache. The local state here reads from it.
        } catch (e) {
            console.warn("Deep fetch error:", e);
        }
    };

    const fetchPosts = async (currentOffset: number, isInitial = false) => {
        if (isInitial && !posts.length) setLoading(true);
        else if (!isInitial) setLoadingMore(true);

        try {
            const data = await socialService.getPosts(LIMIT, currentOffset);
            const newPosts = Array.isArray(data) ? data : (data?.data || []);
            
            if (isInitial) {
                setPosts(newPosts);
                setLocalCache('community_feed_posts', newPosts);
            } else {
                setPosts(prev => [...prev, ...newPosts]);
            }

            if (newPosts.length < LIMIT) {
                setHasMore(false);
            }

            // Fire deep fetch in background
            deepFetchPostsData(newPosts);

        } catch (err) {
            console.error("Error fetching community posts:", err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadMore = () => {
        const newOffset = offset + LIMIT;
        setOffset(newOffset);
        fetchPosts(newOffset, false);
    };

    const handleLike = async (post: any, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user) return;
        
        // Optimistic UI for interactions
        const isLiked = userInteractions[post.id]?.like;
        setUserInteractions(prev => ({
            ...prev,
            [post.id]: { ...prev[post.id], like: !isLiked }
        }));

        // Optimistic UI for counts
        setPosts(prev => prev.map(p => 
            p.id === post.id 
                ? { ...p, likes_count: (p.likes_count || 0) + (isLiked ? -1 : 1) } 
                : p
        ));

        try {
            await socialService.toggleInteraction(user.id, post.id, 'like');
        } catch (err) {
            console.error("Like error:", err);
            setUserInteractions(prev => ({
                ...prev,
                [post.id]: { ...prev[post.id], like: isLiked }
            }));
            setPosts(prev => prev.map(p => 
                p.id === post.id 
                    ? { ...p, likes_count: (p.likes_count || 0) + (isLiked ? 1 : -1) } 
                    : p
            ));
        }
    };

    const handleSave = async (post: any, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user) return;

        const isSaved = userInteractions[post.id]?.save;
        setUserInteractions(prev => ({
            ...prev,
            [post.id]: { ...prev[post.id], save: !isSaved }
        }));

        try {
            await socialService.toggleInteraction(user.id, post.id, 'save');
        } catch (err) {
            console.error("Save error:", err);
            setUserInteractions(prev => ({
                ...prev,
                [post.id]: { ...prev[post.id], save: isSaved }
            }));
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20 px-4">
            
            <div className="space-y-4">
                <AppBackButton fallbackTo="/" />
                <StudyPageHeader
                    tourKey="community_feed"
                    title={t('hubs.community.title', 'Comunidade')}
                    actions={
                      <>
                      <CommunityLinks current="feed" />
                      </>
                    }
                />
            </div>

            {/* Posts Feed */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-32 space-y-4">
                    <Loader2 className="animate-spin text-primary w-12 h-12" />
                    <p className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-400">{t('hubs.community.feed.loading', 'Carregando novidades...')}</p>
                </div>
            ) : posts.length === 0 ? (
                <div data-tour="feed-posts" className="bg-surface px-8 py-16 sm:p-20 rounded-[32px] border border-black/[0.06] dark:border-white/[0.06] text-center space-y-6">
                    <div className="w-20 h-20 bg-tint-butter rounded-full flex items-center justify-center mx-auto text-ink">
                        <AnimatedIcon motion="pop" active><MessageCircle size={28} strokeWidth={1.5} /></AnimatedIcon>
                    </div>
                    <div>
                        <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('hubs.community.feed.empty_title', 'O feed está silencioso...')}</h3>
                        <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">{t('hubs.community.feed.empty_desc', 'Seja o primeiro a compartilhar uma citação marcante!')}</p>
                    </div>
                </div>
            ) : (
                <div data-tour="feed-posts" className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {posts.map((post) => {
                        const parsed = extractPostStyle(post.content);
                        const firstImg = post.media_url || (parsed.style?.imgs ? Object.values(parsed.style.imgs)[0] : null);
                        
                        return (
                            <div 
                                key={post.id}
                                className="group bg-surface rounded-[32px] border border-black/[0.06] dark:border-white/[0.06] hover:-translate-y-1 transition-all duration-500 flex flex-col overflow-hidden"
                            >
                                {/* Author Info */}
                                <div className="p-6 flex items-center justify-between">
                                    <div 
                                        className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            if (post.profiles?.username) {
                                                const { profileService } = await import('../services/profileService');
                                                await profileService.refreshTargetProfile(post.profiles.username);
                                                navigate(`/profile/${post.profiles.username}`);
                                            }
                                        }}
                                    >
                                        <div className="w-10 h-10 rounded-2xl overflow-hidden border border-primary/20 bg-slate-50 dark:bg-slate-800">
                                            {post.profiles?.avatar_config ? (
                                                <AvatarRenderer 
                                                    config={typeof post.profiles.avatar_config === 'string' ? JSON.parse(post.profiles.avatar_config) : post.profiles.avatar_config} 
                                                    seed={post.profiles?.username} 
                                                />
                                            ) : (
                                                <img
                                                    src={post.profiles?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${post.profiles?.display_name || 'U'}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`}
                                                    className="w-full h-full object-cover"
                                                    alt="avatar"
                                                    onError={e => { (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/initials/svg?seed=${post.profiles?.display_name || 'U'}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`; }}
                                                />
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-semibold text-ink flex items-center gap-2">
                                                {post.profiles?.display_name || t('common.user', 'Usuário')}
                                                {post.profiles?.plan_type === 'pro' && <Sparkles size={12} className="text-primary fill-primary" />}
                                            </h4>
                                            <p className="text-[10px] text-slate-400 font-semibold">@{post.profiles?.username}</p>
                                        </div>
                                    </div>
                                    <span className="text-[9px] font-normal text-slate-300 uppercase font-mono tracking-[0.14em]">
                                        {new Date(post.created_at).toLocaleDateString()}
                                    </span>
                                </div>

                                {/* Card Preview - SNAPSHOT STYLE */}
                                <div className="px-4 pb-4">
                                    <div 
                                        className="aspect-[9/16] rounded-3xl overflow-hidden relative border border-black/[0.06] dark:border-white/[0.06] transition-transform duration-500 cursor-pointer active:scale-[0.98]"
                                        style={{ backgroundColor: parsed.style?.bg || '#1a0533' }}
                                        onClick={(e) => { e.stopPropagation(); navigate(`/community-feed/${post.id}`, { replace: true }); setSelectedPost(post); }}
                                    >
                                        {firstImg ? (
                                            <img src={firstImg as string} className="w-full h-full object-cover" alt="" />
                                        ) : (
                                            <div 
                                                className="w-full h-full p-8 flex flex-col justify-center items-center text-center gap-4 relative overflow-hidden"
                                                style={{ 
                                                    fontFamily: parsed.style?.fId || 'inherit',
                                                    color: parsed.style?.color || '#ffffff'
                                                }}
                                            >
                                                <Quote className="w-12 h-12 opacity-10 absolute top-10 left-10 -rotate-12" />
                                                <p className="text-xl md:text-2xl font-medium leading-tight italic drop-shadow-lg">
                                                    "{parsed.text.slice(0, 150)}{parsed.text.length > 150 ? '...' : ''}"
                                                </p>
                                                <div className="w-12 h-px bg-white/20 my-4" />
                                                <p className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] opacity-60">{post.book_title}</p>
                                            </div>
                                        )}
                                        
                                        {/* Overlay for Visual Polish */}
                                        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                                    </div>
                                </div>

                                {/* Footer Stats */}
                                <div className="p-6 pt-2 flex items-center gap-6 border-t border-slate-50 dark:border-slate-800/50 mt-auto">
                                    <button 
                                        onClick={(e) => handleLike(post, e)}
                                        className={`flex items-center gap-2 transition-colors group/btn ${userInteractions[post.id]?.like ? 'text-bad' : 'text-slate-400 hover:text-bad'}`}
                                    >
                                        <Heart size={18} className={`group-hover/btn:scale-125 transition-transform ${userInteractions[post.id]?.like ? 'fill-bad' : ''}`} />
                                        <span className="text-[10px] font-semibold">{post.likes_count || 0}</span>
                                    </button>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); navigate(`/community-feed/${post.id}`, { replace: true }); setSelectedPost(post); }}
                                        className="flex items-center gap-2 text-slate-400 hover:text-primary transition-colors group/btn"
                                    >
                                        <MessageCircle size={18} className="group-hover/btn:scale-125 transition-transform" />
                                        <span className="text-[10px] font-semibold">{post.comments_count || 0}</span>
                                    </button>
                                    <button 
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-slate-400 hover:text-ok transition-colors"
                                    >
                                        <Repeat2 size={18} />
                                    </button>
                                    <button 
                                        onClick={(e) => handleSave(post, e)}
                                        className={`ml-auto transition-colors ${userInteractions[post.id]?.save ? 'text-primary' : 'text-slate-400 hover:text-primary'}`}
                                    >
                                        <Bookmark size={18} className={userInteractions[post.id]?.save ? 'fill-primary' : ''} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    
                    {loadingMore && (
                        <div className="col-span-1 md:col-span-2 flex justify-center py-8">
                            <Loader2 className="animate-spin text-primary w-8 h-8" />
                        </div>
                    )}
                </div>
            )}

            {/* Detailed Modal */}
            {selectedPost && (
                <PostDetailModal 
                    post={selectedPost} 
                    onClose={() => {
                        setSelectedPost(null);
                        navigate('/community-feed', { replace: true });
                    }}
                    onUpdate={fetchPosts}
                />
            )}
        </div>
    );
};

export default CommunityFeedPage;
